import React, { useState, useMemo } from 'react'
import {
  Tr,
  Td,
  Button,
  useDisclosure,
  Collapse,
  Box,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Flex,
  IconButton,
  Badge,
  useToast,
  Avatar,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
} from '@chakra-ui/react'
import { FiChevronDown, FiChevronUp, FiCheck, FiDownload, FiGift } from 'react-icons/fi'
import { useRoleta } from '../hooks/RoletaContext'
import { BotaoWhatsApp } from './BotaoWhatsApp'

const formatarTelefone = (telefone) => {
  if (!telefone) return ''

  const numeroLimpo = telefone.replace(/\D/g, '')

  if (numeroLimpo.length === 10) {
    return `(${numeroLimpo.substring(0, 2)}) ${numeroLimpo.substring(2, 6)}-${numeroLimpo.substring(6)}`
  } else if (numeroLimpo.length === 11) {
    return `(${numeroLimpo.substring(0, 2)}) ${numeroLimpo.substring(2, 7)}-${numeroLimpo.substring(7)}`
  } else {
    return telefone
  }
}

const LinhaClienteComponent = ({ cliente }) => {
  const hasCaricatura = Boolean(cliente.link_ilustracao)

  const { premios, atribuirPremioCliente, isAtribuindoPremio, confirmarBrindeEntregue, isConfirmandoBrindeEntregue } = useRoleta()
  const { isOpen, onToggle } = useDisclosure()
  const { isOpen: isOpenDialog, onOpen: onOpenDialog, onClose: onCloseDialog } = useDisclosure()
  const [premiSelecionado, setPremiSelecionado] = useState('')
  const toast = useToast()
  const cancelRef = React.useRef()

  const premioJaEnviado = Boolean(cliente.premio_enviado)
  const brindeEntregue = Boolean(cliente.premio_entregue)

  const premiosFiltrados = useMemo(() => {
    if (!hasCaricatura) {
      return premios.filter((premio) => !premio.nome_premio.toLowerCase().includes('caricatura'))
    }
    return premios
  }, [premios, hasCaricatura])

  const handleToggle = () => {
    if (premioJaEnviado) {
      toast({
        title: 'Prêmio já enviado',
        description: 'Este cliente já recebeu um prêmio.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    onToggle()
  }

  const handleConfirmarPremio = async () => {
    if (!premiSelecionado) {
      toast({
        title: 'Selecione um prêmio',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      await atribuirPremioCliente(cliente.codigo_cliente, premiSelecionado)
      toast({
        title: 'Prêmio atribuído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      onToggle()
    } catch (error) {
      toast({
        title: 'Erro ao atribuir prêmio',
        description: error.message || 'Tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleClickPremio = (premioId) => {
    setPremiSelecionado(premioId)
  }

  const handleConfirmarBrindeEntregue = async () => {
    try {
      await confirmarBrindeEntregue(cliente.codigo_cliente)
      toast({
        title: 'Brinde confirmado como entregue',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      onCloseDialog()
    } catch (error) {
      toast({
        title: 'Erro ao confirmar entrega',
        description: error.message || 'Tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const getBgColor = () => {
    if (brindeEntregue) return 'purple.50'
    if (premioJaEnviado) return 'green.50'
    return undefined
  }

  return (
    <>
      <Tr bg={getBgColor()}>
        <Td p={2}>
          {premioJaEnviado ? (
            <Tooltip label="Prêmio já enviado" fontSize="sm">
              <Box display="inline-block">
                <IconButton aria-label="Prêmio já enviado" icon={<FiCheck />} size="sm" variant="ghost" colorScheme="green" isDisabled />
              </Box>
            </Tooltip>
          ) : (
            <IconButton
              aria-label={isOpen ? 'Fechar detalhes' : 'Ver detalhes'}
              icon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
              size="sm"
              onClick={handleToggle}
              variant="ghost"
            />
          )}
        </Td>
        <Td>
          <Flex align="center" gap={3}>
            {cliente.link_foto && <Avatar size="sm" src={cliente.link_foto} name={cliente.nome_cliente} />}
            {cliente.nome_cliente}
            {premioJaEnviado && (
              <Badge colorScheme="green" ml={2}>
                Prêmio enviado
              </Badge>
            )}
            {brindeEntregue && (
              <Badge colorScheme="purple" ml={2}>
                Brinde entregue
              </Badge>
            )}
          </Flex>
        </Td>
        <Td>{cliente.empresa_cliente}</Td>
        <Td>{formatarTelefone(cliente.telefone_cliente)}</Td>
        <Td>{cliente.email_cliente}</Td>
        <Td>
          <Badge colorScheme="blue" fontSize="0.9em" px={2} py={1}>
            {cliente.codigo_cliente}
          </Badge>
        </Td>
        <Td>
          <Flex>
            <BotaoWhatsApp cliente={cliente} />

            {hasCaricatura && (
              <Tooltip label="Baixar Caricatura" fontSize="sm">
                <IconButton
                  as="a"
                  download
                  aria-label="Baixar Caricatura"
                  icon={<FiDownload />}
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  href={`https://api.innovationbrindes.com.br/api/site/v2/fluxo-roleta/baixar-imagem-ilustracao-producao?codigo_cliente=${cliente.codigo_cliente}`}
                  ml={2}
                />
              </Tooltip>
            )}

            <Tooltip label="Brinde Entregue" fontSize="sm">
              <IconButton
                aria-label="Brinde Entregue"
                icon={<FiGift />}
                size="sm"
                variant="outline"
                colorScheme="purple"
                ml={2}
                onClick={onOpenDialog}
                isDisabled={brindeEntregue}
              />
            </Tooltip>
          </Flex>

          {/* Diálogo de confirmação para brinde entregue */}
          <AlertDialog isOpen={isOpenDialog} leastDestructiveRef={cancelRef} onClose={onCloseDialog}>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Confirmar Entrega de Brinde
                </AlertDialogHeader>

                <AlertDialogCloseButton />

                <AlertDialogBody>Confirmar que o brinde foi entregue para {cliente.nome_cliente}?</AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onCloseDialog}>
                    Cancelar
                  </Button>
                  <Button
                    colorScheme="purple"
                    ml={3}
                    isLoading={isConfirmandoBrindeEntregue}
                    loadingText="Confirmando..."
                    onClick={handleConfirmarBrindeEntregue}
                  >
                    Confirmar Entrega
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </Td>
      </Tr>
      {!premioJaEnviado && (
        <Tr>
          <Td colSpan={7} p={0}>
            <Collapse in={isOpen} animateOpacity>
              <Box p={4} bg="gray.50" borderTop="1px" borderColor="gray.200">
                <Flex mb={4} gap={3}>
                  <Box>
                    <Text fontWeight="bold" mb={3}>
                      Selecione o prêmio para {cliente.nome_cliente}:
                    </Text>
                  </Box>
                  {cliente.link_ilustracao && (
                    <Box ml="auto">
                      <img
                        src={cliente.link_ilustracao}
                        alt="Ilustração do cliente"
                        style={{
                          maxHeight: '100px',
                          maxWidth: '100px',
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                  )}
                </Flex>

                <RadioGroup value={premiSelecionado} onChange={setPremiSelecionado}>
                  <Stack spacing={3}>
                    {premiosFiltrados.map((premio) => (
                      <Box
                        key={premio.id}
                        p={2}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={premiSelecionado === premio.id ? 'blue.500' : 'gray.200'}
                        bg={premiSelecionado === premio.id ? 'blue.50' : 'white'}
                        cursor="pointer"
                        onClick={() => handleClickPremio(premio.id)}
                      >
                        <Flex align="center" width="100%">
                          <Radio
                            value={premio.id}
                            isChecked={premiSelecionado === premio.id}
                            mr={3}
                            onClick={(e) => e.stopPropagation()} // Evita dupla seleção
                          />
                          <Flex justify="space-between" flex="1" align="center">
                            <Text fontWeight="medium">{premio.nome_premio}</Text>
                            <Text fontSize="sm" color="gray.600" ml={4}>
                              Disponíveis hoje: {premio.qtd_dia}
                            </Text>
                          </Flex>
                        </Flex>
                      </Box>
                    ))}
                  </Stack>
                </RadioGroup>
                <Flex justifyContent="flex-end" mt={4}>
                  <Button colorScheme="blue" onClick={handleConfirmarPremio} isLoading={isAtribuindoPremio} isDisabled={!premiSelecionado}>
                    Confirmar prêmio
                  </Button>
                </Flex>
              </Box>
            </Collapse>
          </Td>
        </Tr>
      )}
    </>
  )
}

export const LinhaCliente = React.memo(LinhaClienteComponent)
